import { efemService } from '../services/EfemService';

export interface EfemSlotMapSlotSummary {
  slotNumber: number;
  isOccupied: boolean;
  waferId?: string | null;
}

interface CacheEntry {
  slots: EfemSlotMapSlotSummary[];
  fetchedAt: number;
}

/** In-wizard cache TTL — avoids repeated edge round-trips when changing steps. */
const DEFAULT_TTL_MS = 120_000;

const cache = new Map<string, CacheEntry>();

export function efemSlotMapCacheKey(gatewaySerialNumber: string, loadPortId: number): string {
  return `${gatewaySerialNumber}:${loadPortId}`;
}

export function getCachedEfemSlotMap(
  gatewaySerialNumber: string,
  loadPortId: number,
  ttlMs: number = DEFAULT_TTL_MS,
): EfemSlotMapSlotSummary[] | null {
  const key = efemSlotMapCacheKey(gatewaySerialNumber, loadPortId);
  const entry = cache.get(key);
  if (!entry) {
    return null;
  }
  if (Date.now() - entry.fetchedAt > ttlMs) {
    cache.delete(key);
    return null;
  }
  return entry.slots;
}

export function setCachedEfemSlotMap(
  gatewaySerialNumber: string,
  loadPortId: number,
  slots: EfemSlotMapSlotSummary[],
): void {
  cache.set(efemSlotMapCacheKey(gatewaySerialNumber, loadPortId), {
    slots,
    fetchedAt: Date.now(),
  });
}

export function invalidateEfemSlotMapsForGateway(gatewaySerialNumber: string): void {
  const prefix = `${gatewaySerialNumber}:`;
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
}

export function clearEfemSlotMapCache(): void {
  cache.clear();
}

/**
 * Returns cached slot map when fresh; otherwise fetches from EFEM and caches the result.
 */
export async function fetchEfemSlotMapCached(
  gatewaySerialNumber: string,
  loadPortId: number,
  options?: { force?: boolean; ttlMs?: number },
): Promise<EfemSlotMapSlotSummary[]> {
  if (!options?.force) {
    const cached = getCachedEfemSlotMap(gatewaySerialNumber, loadPortId, options?.ttlMs);
    if (cached) {
      return cached;
    }
  }

  const response = await efemService.getEfemSlotMap(gatewaySerialNumber, loadPortId);
  const slots: EfemSlotMapSlotSummary[] = response.slots.map((s) => ({
    slotNumber: s.slotNumber,
    isOccupied: s.isOccupied,
    waferId: s.waferId ?? null,
  }));
  setCachedEfemSlotMap(gatewaySerialNumber, loadPortId, slots);
  return slots;
}
